import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { Toast, ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed bottom-24 xl:bottom-6 right-4 xl:right-6 z-50 flex flex-col gap-3 pb-[env(safe-area-inset-bottom)]">
      @for (toast of toasts; track toast.id) {
        <div
          class="bg-white rounded-lg shadow-xl p-4 max-w-sm w-80 overflow-hidden animate-slide-in-right"
          [ngClass]="{
            'border-l-4 border-green-500': toast.type === 'success',
            'border-l-4 border-red-500': toast.type === 'error',
            'border-l-4 border-blue-500': toast.type === 'info',
            'border-l-4 border-amber-500': toast.type === 'warning'
          }">

          <div class="flex items-start">
            <!-- Icon -->
            <div class="flex-shrink-0">
              <!-- Success icon -->
              @if (toast.type === 'success') {
                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              }
              <!-- Error icon -->
              @if (toast.type === 'error') {
                <div class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
              }
              <!-- Info icon -->
              @if (toast.type === 'info') {
                <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"></path>
                  </svg>
                </div>
              }
              <!-- Warning icon -->
              @if (toast.type === 'warning') {
                <div class="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86l-8.6 14.86A1 1 0 002.54 20h16.92a1 1 0 00.85-1.28l-8.6-14.86a1 1 0 00-1.42 0z"></path>
                  </svg>
                </div>
              }
            </div>

            <!-- Content -->
            <div class="ml-3 flex-1 min-w-0">
              <h3 class="text-sm font-medium text-gray-900 truncate">{{ toast.title }}</h3>
              <p class="mt-1 text-sm text-gray-600 line-clamp-2">{{ toast.message }}</p>
            </div>

            <!-- Close button -->
            <button
              (click)="dismiss(toast.id)"
              class="ml-3 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Progress bar -->
          <div class="mt-3 -mx-4 -mb-4 h-1 bg-gray-100">
            <div
              class="h-full transition-all duration-100 ease-linear"
              [ngClass]="{
                'bg-green-500': toast.type === 'success',
                'bg-red-500': toast.type === 'error',
                'bg-blue-500': toast.type === 'info',
                'bg-amber-500': toast.type === 'warning'
              }"
              [style.width.%]="toast.progress">
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: contents;
    }

    .animate-slide-in-right {
      animation: slideInRight 0.3s ease-out;
    }

    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private subscription?: Subscription;

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.subscription = this.toastService.toasts$.subscribe(toasts => {
      this.toasts = toasts;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  dismiss(id: number): void {
    this.toastService.dismiss(id);
  }
}
